import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import * as React from 'react';
import TinyColor from 'tinycolor2';
import { UXPContext } from '@core/context/Context';
import { getInputSizeStyles, getInputStateClass } from '../utils';
import { Dropdown } from '../../../popups/Dropdown';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import DropdownIndicator from '@widget/other/DropdownIndicator';
import Input from '../Input';
import ColorPallet from './ColorPallet';
/**
 *
 * @export
 * Color picker input field
 */
const ColorPicker = (props) => {
    const context = React.useContext(UXPContext);
    const { color, onChange, className, displayFormat = 'hex8', returnFormat = 'hex8', minWidth, maxWidth, width, fullWidth, disabled, readOnly, isValid: isValidProp, dropdownMaxWidth, dropdownMinWidth, placeholder, dropdownClassname, hideLabels, onClear } = props;
    const [showPicker, setShowPicker] = React.useState(false);
    const [showInput, setShowInput] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);
    const [manualValue, setManualValue] = React.useState('');
    const triggerRef = React.useRef(null);
    const Color = TinyColor(color);
    React.useEffect(() => {
        if (Color.isValid()) {
            setManualValue(getColor(Color, 'display'));
        }
        else {
            setManualValue('');
        }
    }, [color, displayFormat]);
    const handleFocus = () => {
        if (!disabled && !readOnly) {
            setIsFocused(true);
        }
    };
    const handleBlur = () => {
        if (!disabled && !readOnly) {
            setIsFocused(false);
        }
    };
    const handleKeyDownOnTrigger = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowPicker(true);
        }
    };
    const handleTriggerClick = () => {
        if (!disabled && !readOnly) {
            setShowPicker(!showPicker);
        }
    };
    const closePicker = () => {
        setShowPicker(false);
        setShowInput(false);
        setTimeout(() => { var _a; return (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 100);
    };
    const handleManualInputChange = (val) => {
        setManualValue(val);
    };
    const handleManualInputBlur = () => {
        const testColor = TinyColor(manualValue);
        if (testColor.isValid()) {
            onChange(getColor(testColor, 'return'));
        }
        else {
            // Revert to current valid value
            if (Color.isValid()) {
                setManualValue(getColor(Color, 'display'));
            }
            else {
                setManualValue('');
            }
        }
    };
    const onColorChange = (c) => {
        const colorObj = TinyColor(c);
        onChange(getColor(colorObj, 'return'));
    };
    function getColor(color, type) {
        if (!color.isValid())
            return "";
        let format = (type == 'display') ? displayFormat : returnFormat;
        return color.toString(format);
    }
    const renderDropdownContent = () => (_jsxs("div", { className: "uxpcore_colorpicker__dropdown-content", children: [_jsxs("div", { className: "uxpcore_colorpicker__header", children: [_jsx("div", { className: "uxpcore_colorpicker__title", children: showInput ? context.$L('uxp-core.text.enter-color') : context.$L('uxp-core.text.select-a-color') }), _jsx("div", { className: "uxpcore_colorpicker__toggle-input", onClick: (e) => {
                            e.stopPropagation();
                            setShowInput(!showInput);
                        }, title: showInput ? context.$L('uxp-core.text.show-palette') : context.$L('uxp-core.text.type-color'), children: _jsx(FontAwesomeIconWrapper, { icon: ['far', showInput ? 'palette' : 'keyboard'] }) })] }), showInput && (_jsx("div", { className: "uxpcore_colorpicker__input-container", children: _jsx(Input, { type: "text", value: manualValue, onChange: handleManualInputChange, onBlur: handleManualInputBlur, placeholder: "#RRGGBB or rgba(r,g,b,a)", fullWidth: true, hideClear: true }) })), !showInput && (_jsx(ColorPallet, { color: color, onChange: onColorChange })), _jsx("div", { className: "uxpcore_colorpicker__footer", children: _jsx("button", { className: "uxpcore_colorpicker__done-btn", onClick: closePicker, children: context.$L('uxp-core.text.done') }) })] }));
    const containerStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined });
    const dropdownIndicator = _jsx(DropdownIndicator, { isOpen: showPicker });
    const stateClass = getInputStateClass({ disabled, readOnly, isValid: isValidProp });
    const clearButton = (!!color && Color.isValid() && !disabled && !readOnly) ? (_jsx("div", { className: "uxpcore_input__clear-btn", onClick: (e) => { e.stopPropagation(); onClear ? onClear() : onChange(''); }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'times'] }) })) : null;
    return (_jsxs("div", { className: classNames("uxpcore_colorpicker", className, { "uxpcore_input--full-width": fullWidth }), style: containerStyles, children: [_jsxs("div", { ref: triggerRef, className: classNames("uxpcore_input", "uxpcore_input--colorpicker", stateClass, {
                    "uxpcore_input--has-prefix": !hideLabels,
                    "uxpcore_input--has-suffix": !!(clearButton || dropdownIndicator),
                    "uxpcore_input--has-content": !!color && Color.isValid(),
                    "uxpcore_input--focus": isFocused || showPicker,
                    "uxpcore_input--full-width": fullWidth
                }), onClick: handleTriggerClick, onKeyDown: handleKeyDownOnTrigger, onFocus: handleFocus, onBlur: handleBlur, tabIndex: disabled || readOnly ? -1 : 0, style: getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined }), children: [!hideLabels && (_jsx("div", { className: "uxpcore_input__prefix", children: _jsx("div", { className: "uxpcore_colorpicker__color-dot", style: { backgroundColor: Color.isValid() ? color : 'transparent' } }) })), _jsx("div", { className: "uxpcore_input__value", children: Color.isValid()
                            ? getColor(Color, 'display')
                            : (placeholder || context.$L('uxp-core.text.select-a-color')) }), _jsxs("div", { className: "uxpcore_input__suffix", children: [clearButton, dropdownIndicator] })] }), _jsx(Dropdown, { externalTriggerRef: triggerRef, content: renderDropdownContent(), position: "bottom-left", isOpen: showPicker, onToggle: closePicker, contentClassName: classNames("uxpcore_colorpicker__dropdown", dropdownClassname), matchTriggerWidth: false, maxWidth: dropdownMaxWidth, minWidth: dropdownMinWidth || '15rem' })] }));
};
export default ColorPicker;
